import { useAccountAccesses, useRecaptchaInfo } from '@/api/auth';
import { useGetIsAdmin } from '@/api/config';
import { useGetActivityCategories, useGetProfile, useGetProvinces } from '@/api/profile';
import { useGetStats } from '@/api/stats';
import { setSentryUser } from '@/helpers/sentry';
import { Access, AccessComponentType } from '@/pages/Accounts/Accounts.types.ts';
import type { ActivityCategory, Profile, Province } from '@/pages/Profile/Profile.types';
import { ExtraInformationStatusType } from '@/pages/Profile/Profile.types';
import { FreeServiceDetail } from '@/pages/Service/Service.types.ts';
import type { StatsResponse } from '@/pages/Stats/Stats.types';
import { defineStore } from 'pinia';

export const useAppStore = defineStore('app', () => {
  const isAdmin = ref(false);
  const recaptchaInfo = ref({
    siteKey: '',
    status: false,
  });
  const stats: Ref<StatsResponse | undefined> = ref();
  const profileExtraInformationStatus = ref<ExtraInformationStatusType>();
  const profile = ref<Profile>();
  const activityCategories: Ref<ActivityCategory[] | undefined> = ref();
  const provinces: Ref<Province[] | undefined> = ref();
  const isNewUserWelcomeModalOpen = ref(false);
  const isLoading = ref(false);
  const freeServiceDetail = ref<FreeServiceDetail | undefined>(undefined);
  const userACL = ref<Access[] | 'No_ACL' | undefined>(undefined);

  const getRecaptchaInfo = async () => {
    const { data, error } = await useRecaptchaInfo();

    if (error || !data) return;

    recaptchaInfo.value = {
      siteKey: data.result.google_recaptcha_sitekey,
      status: data.result.google_recaptcha_status,
    };
  };

  const getIsAdmin = async () => {
    // Reset isAdmin
    isAdmin.value = false;

    const { data, error } = await useGetIsAdmin();

    if (error || !data) return;

    isAdmin.value = data.data.isAdmin;
  };

  const getStats = async () => {
    // Reset stats
    stats.value = undefined;

    const { data, error } = await useGetStats();

    if (error || !data) return;

    stats.value = data.data;
    profileExtraInformationStatus.value = data.data.extraInformationStatus;
  };

  const getProfile = async () => {
    // Reset profile
    profile.value = undefined;

    const { data, error } = await useGetProfile();

    if (error || !data) return;

    profile.value = data.data;

    // Set Sentry user context for better error tracking
    setSentryUser({
      username: data.data.userName,
      email: data.data.email,
    });
  };

  const getProvinces = async () => {
    // Reset provinces
    provinces.value = undefined;

    const { data, error } = await useGetProvinces();

    if (error || !data) return;

    provinces.value = data.data;
  };

  const getActivityCategories = async () => {
    const { data, error } = await useGetActivityCategories();

    if (error || !data) return;

    activityCategories.value = data.data;
  };

  const getAllData = async () => {
    isLoading.value = true;

    await Promise.allSettled([getIsAdmin(), getStats(), getActivityCategories()]);

    isLoading.value = false;
  };

  const getUserACL = async () => {
    if (userACL.value === undefined) {
      const { data, error } = await useAccountAccesses();

      if (data && data.data) {
        userACL.value = data.data;
      }

      if (error) {
        if (error.code === 'USER_NOT_FOUND') {
          userACL.value = 'No_ACL';
        }
      }
    }
    return;
  };

  const resetAllData = () => {
    isAdmin.value = false;
    stats.value = undefined;
    profileExtraInformationStatus.value = undefined;
    profile.value = undefined;
    activityCategories.value = undefined;
    provinces.value = undefined;
  };

  const canWrite = (componentName: AccessComponentType): boolean => {
    if (userACL.value === 'No_ACL') {
      return true;
    }

    if (userACL.value === undefined) {
      return false;
    }

    const componentAccess = userACL.value.find(
      (access: Access) => access.component === componentName && access.isActive,
    );

    if (!componentAccess) {
      return false;
    }

    return componentAccess.action.includes('w');
  };

  const canRead = (componentName: AccessComponentType): boolean => {
    if (userACL.value === 'No_ACL') {
      return true;
    }

    if (userACL.value === undefined) {
      return false;
    }

    const componentAccess = userACL.value.find(
      (access: Access) => access.component === componentName && access.isActive,
    );

    if (!componentAccess) {
      return false;
    }

    return componentAccess.action.includes('r');
  };

  return {
    canWrite,
    canRead,
    getStats,
    getAllData,
    getProfile,
    getUserACL,
    getProvinces,
    resetAllData,
    getRecaptchaInfo,
    getActivityCategories,
    stats,
    isAdmin,
    userACL,
    profile,
    provinces,
    isLoading,
    recaptchaInfo,
    freeServiceDetail,
    activityCategories,
    isNewUserWelcomeModalOpen,
    profileExtraInformationStatus,
  };
});
